import React from 'react';
import MemberRankBadge from '../Badge/MemberRankBadge';
import { formatPrice, getFilePath } from 'utils';
import { SaleOrder, SaleOrderStatusCode } from 'models/SaleOrder';
import dayjs from 'dayjs';
import ProductItem from 'components/PurchaseOrder/ProductItem';
import { useGetSaleOrderByIdQuery } from 'services';
import { useProductImagesMap } from 'hooks/useProductImagesMap';

const InfoField: React.FC<{
  label: string;
  value: React.ReactNode;
  isHighlight?: boolean;
  isDelayed?: boolean;
  isValueBold?: boolean;
  className?: string;
}> = ({ label, value, isHighlight = false, isDelayed, isValueBold, className }) => {
  return (
    <div className={className}>
      <div className="flex items-center justify-start font-['Inter']">
        <div className={'text-gray-500 mb-1 text-sm'}>{label}</div>
      </div>
      <div
        className={`font-['Inter'] font-medium ${
          isHighlight
            ? 'text-blue-500 !font-bold '
            : isDelayed
              ? 'text-red-500 !font-bold '
              : isValueBold
                ? 'font-bold'
                : ''
        }`}
      >
        {value || '-'}
      </div>
    </div>
  );
};

// Map statusId to display text
export const mapStatusToDisplay = (statusCode?: SaleOrderStatusCode): { text: string; color: string } => {
  switch (statusCode) {
    case SaleOrderStatusCode.DEBT:
      return { text: 'Đã chuyển công nợ', color: 'bg-[var(--sematicErrorColor500)]' };
    case SaleOrderStatusCode.CANCELLED:
      return { text: 'Đã hủy', color: 'bg-gray-500' };
    case SaleOrderStatusCode.PAID:
      return { text: 'Đã thanh toán', color: 'bg-[var(--sematicSuccessColor500)]' };
    case SaleOrderStatusCode.DRAFT:
      return { text: 'Đã lưu nháp', color: 'bg-gray-500' };
    default:
      return { text: 'Không xác định', color: 'bg-gray-500' };
  }
};

const SaleOrderDoneInfo: React.FC<{ saleOrderId: string }> = ({ saleOrderId }) => {
  const { data: saleOrderData } = useGetSaleOrderByIdQuery({
    id: saleOrderId,
    query: {
      include: 'status|customer.baseUser|saleOrderItems.saleItemDimensions',
      includeRef: 'Debt',
    },
  });

  const [saleOrder, setSaleOrder] = React.useState<SaleOrder | null>(null);
  React.useEffect(() => {
    if (saleOrderData) {
      setSaleOrder(saleOrderData.data);
    }
  }, [saleOrderData]);
  const productVersionIds = saleOrder?.saleOrderItems
    ? (saleOrder.saleOrderItems.map((item) => item.productVersionId).filter(Boolean) as string[])
    : [];
  const { imagesMap: productImagesMap } = useProductImagesMap(productVersionIds);
  const getImageUrl = (productVersionId: string) => {
    const imagePaths = productImagesMap?.[productVersionId];
    return imagePaths && imagePaths.length > 0 ? getFilePath() + '/' + imagePaths[0] : undefined;
  };
  const formattedDate = saleOrder?.createdAt ? dayjs(saleOrder?.createdAt).format('HH:mm Ngày DD/MM/YYYY') : '-';

  // Get status display info
  const statusDisplay = mapStatusToDisplay(saleOrder?.status?.code as SaleOrderStatusCode);

  const memberRank = 'Member Ship';
  const memberPoints = Number(saleOrder?.customer?.points) || 0;

  return (
    <div className='p-6 pb-3 bg-white rounded-lg min-h-[calc(100vh-100px)] overflow-y-auto'>
      <h2 className='text-[20px] leading-6 font-bold mb-6'>THÔNG TIN ĐƠN HÀNG</h2>

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-4 p-3'>
        <InfoField label='Mã đơn hàng' value={saleOrder?.code} className='col-span-1' />

        <InfoField
          label='Trạng thái'
          value={
            <span className={`${statusDisplay.color} text-white px-4 py-1 rounded-full text-sm`}>
              {statusDisplay.text}
            </span>
          }
          className='col-span-1'
        />

        <InfoField label='Thời gian' value={formattedDate} className='col-span-1' />

        <InfoField
          label='Mã công nợ'
          value={saleOrder?.Debt?.[0]?.code || '---'}
          isHighlight={true}
          className='col-span-1'
        />
        <InfoField label='Khách hàng' value={saleOrder?.customerName} className='col-span-1' />

        <InfoField
          label='Mã khách hàng'
          value={saleOrder?.customer?.baseUser?.code}
          isHighlight={true}
          className='col-span-1'
        />

        <InfoField label='Số điện thoại' value={saleOrder?.customerPhone} className='col-span-1' />

        <InfoField label='Email' value={saleOrder?.customerEmail} isHighlight={true} className='col-span-1' />

        {memberPoints > 0 && (
          <div className='xl:col-span-4 lg:col-span-3 md:col-span-2 flex items-center gap-3 mt-2'>
            <div className='flex items-center gap-6'>
              <span className='text-[var(--textBlack56)]'>Điểm tích lũy:</span>{' '}
              <span className=''>{formatPrice(memberPoints || 0)} VNĐ</span>
              <MemberRankBadge name={memberRank} color='#FF823E' />
            </div>
          </div>
        )}
        <InfoField
          label='Địa chỉ'
          value={saleOrder?.customerAddress}
          className='xl:col-span-4 lg:col-span-3 md:col-span-2'
        />

        {saleOrder?.description && (
          <div className='xl:col-span-4 lg:col-span-3 md:col-span-2 mt-2'>
            <InfoField
              label='Mô tả'
              value={<div className='border rounded-lg p-4 text-gray-700 min-h-[100px]'>{saleOrder?.description}</div>}
              className='xl:col-span-4 lg:col-span-3 md:col-span-2'
            />
          </div>
        )}
      </div>
      <div className='text-[20px] leading-6 font-bold mt-4 mb-2'>DANH SÁCH SẢN PHẨM</div>
      <div className='flex flex-col gap-2'>
        {saleOrder?.saleOrderItems?.map((item) => {
          const saleDimensions = item?.saleDimensions?.split('|').filter((item) => item !== '');
          const dimension = item?.saleItemDimensions
            ?.map((itemSaleDimensions) => {
              const unit = itemSaleDimensions.dimensions?.split('|');
              const value = (saleDimensions || []).map(
                (saleDimension, i) => `${unit[i] || '1'} ${saleDimension || ''}`,
              ); // Ghép từng cặp
              return value.join(' x ');
            })
            .filter((dim) => dim !== '');

          return (
            <ProductItem
              key={item.id}
              id={item.id}
              name={item.name || ''}
              unitPrice={item.salePrice}
              quantity={item.quantity}
              currencyUnit={'VNĐ'}
              unit={item.unit || ''}
              imageUrl={getImageUrl(item.productVersionId)}
              attribute={item.attributeName || ''}
              atrtributePrice={item.attributePrice || 0}
              dimension={dimension}
              isPOS
              isQuickView={true}
            />
          );
        })}
      </div>
    </div>
  );
};

export default SaleOrderDoneInfo;
